import NumberInput from '../../../common/components/Form/NumberInput.js'
import {
  updateLabelConfig,
  labelConfigSelector,
  isLabelPropertyEnabledSelector,
  getInsuredValueLabelProperty,
} from '../../../data/labelInfos/index.js'
import {useSelector} from '../../../store.js'
import {
  CANADA_POST,
  DHL_ECOMMERCE,
  DHL,
  ENDICIA,
  SHIPPO,
} from '../../../common/constants/ShipperNames.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import useLabelProperty, {
  ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC,
  defaultValidLabelPropertiesFunc,
  formatMetaClassName,
} from './useLabelProperty.js'
import className from '../../../common/className.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'

const VALID_SHIPPER_TYPES = [CANADA_POST, DHL_ECOMMERCE, DHL, ENDICIA, SHIPPO]

function labelPropertiesFunc(shipperType) {
  return VALID_SHIPPER_TYPES.includes(shipperType)
    ? [getInsuredValueLabelProperty(shipperType)]
    : []
}

function getDHLInsuredValueError(config) {
  const insuredValue = Number(config.dhl__insured_value)

  if (!insuredValue) {
    return null
  }

  const totalDeclaredValue = config.packages.reduce(
    (prev, {dhl__declared_value}) => prev + (Number(dhl__declared_value) || 0),
    0,
  )

  if (totalDeclaredValue && totalDeclaredValue < insuredValue) {
    return 'Declared value can not be less than insured value'
  }

  return null
}

export function insuredValueErrorSelector(state, {labelInfoID, shipperType}) {
  const labelConfig = labelConfigSelector(state, {labelInfoID})

  if (shipperType === DHL) {
    return getDHLInsuredValueError(labelConfig)
  }

  return null
}

export default function InsuredValue() {
  const {
    labelInfoID,
    shipperType,
    onChange,
    isLabelEnableMode,
    isSummary,
    isPickMode,
  } = useLabelConfigContext()
  const propertySpecs = InsuredValue.propertySpecList[0]
  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  const insuredValue = useSelector(
    (state) => labelConfigSelector(state, {labelInfoID})[labelProperty] || '',
  )
  const error = useSelector((state) =>
    insuredValueErrorSelector(state, {labelInfoID, shipperType}),
  )

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  if (!VALID_SHIPPER_TYPES.includes(shipperType)) {
    return null
  }

  const disabled = isLabelEnableMode && !isLabelPropertyEnabled

  const label = 'Insured Value'

  if (isSummary) {
    return label
  }

  if (isPickMode) {
    return <PickLabelProperty label={label} labelProperty={labelProperty} />
  }

  return (
    <li
      className={className`list__item--shipping-options btm-offset-7 ${formatMetaClassName(
        labelProperty,
      )} ${{
        'property-disabled': disabled,
      }}`}
    >
      <div className="wrap--edit-preset-form-input">
        <div className="flex">
          <label
            className="label--bold label--config__txt"
            htmlFor="insured_value"
          >
            {label}
          </label>
        </div>
        <NumberInput
          type="text"
          id={`${labelInfoID}__${labelProperty}`}
          value={insuredValue}
          disabled={disabled}
          onChange={(value) => {
            updateLabelConfig(labelInfoID, {
              [labelProperty]: value,
            })

            onChange()
          }}
        />
      </div>
      {error && <small className="error">{error}</small>}
      <EnableLabelProperty labelProperty={labelProperty} />
    </li>
  )
}

InsuredValue.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc,
    dependentPropertiesFunc: ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: defaultValidLabelPropertiesFunc,
  },
]
